/**
 * Zweistufiger Vorgang-Status — DOM-freie, unit-testbare Ableitung.
 *
 * Ein Vorgang hat kein eigenes Status-Feld; der Status ergibt sich aus dem
 * Todo-Fortschritt bzw. — ohne Todos — aus einem manuell gesetzten Wert
 * (`statusManuell`). Das Backend hängt den abgeleiteten Wert bereits als
 * `status` an die DTOs (Single Source of Truth); dieser Helfer bevorzugt ihn
 * und dupliziert die Regel nur als Fallback, damit die Anzeige nie leer bleibt.
 */

/** Gültige Vorgang-Status-Werte: offen oder erledigt, nichts dazwischen. */
export const VORGANG_STATUS_VALUES = ["Offen", "Erledigt"] as const;
export type VorgangStatusWert = (typeof VORGANG_STATUS_VALUES)[number];

const STATUS_SET = new Set<string>(VORGANG_STATUS_VALUES);

/** Minimale Felder, aus denen sich der Vorgang-Status ableiten lässt. */
export interface VorgangStatusInput {
    fortschrittErledigt?: number | null;
    fortschrittGesamt?: number | null;
    /** Manueller Status (nur relevant ohne aktive Todos). */
    statusManuell?: string | null;
    /** Serverseitig abgeleiteter Status (Single Source of Truth), falls vorhanden. */
    status?: string | null;
}

/**
 * Zweistufiger Status:
 *  - alle To-dos erledigt → **Erledigt**
 *  - sonst (keine To-dos, teilweise oder gar nicht erledigt) → **Offen**
 *
 * Ohne aktive To-dos (`fortschrittGesamt` ≤ 0) hat ein gültiger `statusManuell`
 * Vorrang (z. B. manuell auf „Erledigt"). Ist bereits ein serverseitig
 * abgeleiteter `status` vorhanden, wird dieser übernommen.
 */
export function deriveVorgangStatus(v: VorgangStatusInput): VorgangStatusWert {
    if (v.status && STATUS_SET.has(v.status)) return v.status as VorgangStatusWert;
    const total = v.fortschrittGesamt ?? 0;
    const done = v.fortschrittErledigt ?? 0;
    if (total > 0 && done >= total) return "Erledigt";
    if (total <= 0 && v.statusManuell && STATUS_SET.has(v.statusManuell)) {
        return v.statusManuell as VorgangStatusWert;
    }
    return "Offen";
}
